import * as d3 from 'd3';
import cn from 'classnames';
import { format, parseISO, set, subDays } from 'date-fns';
import styles from './SourcePage.module.scss';
import { useTranslation } from 'react-i18next';
import Tooltip from '../../components/Tooltip/Tooltip.jsx';
import {Tooltip as ReactTooltip} from 'react-tooltip';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { API } from '../../API';
import { ampli } from "../../ampli";
import { MessagesModal} from './MessagesModal.jsx';
import { Loader } from '../../components/Loader/Loader';
import { LoaderSmall } from '../../components/LoaderSmall/LoaderSmall';
import { Paginator } from '../../components/Paginator/Paginator';
import { ReactComponent as VerifiedTwitter } from './assets/verifiedTwitter.svg';
import { ReactComponent as UserIcon } from './assets/user.svg';
import { ReactComponent as StatsIcon } from './assets/stats.svg';
import { ReactComponent as EngagementIcon } from './assets/stats.svg';
import { ReactComponent as AnchorIcon } from './assets/anchor.svg';
import { ReactComponent as ExpandIcon } from '../../assets/expand.svg';
import { ReactComponent as LinkIcon } from '../../assets/link.svg';
import { ReactComponent as CalendarIcon } from '../../assets/calendar.svg';
import { ReactComponent as ViewsIcon } from '../../assets/views.svg';
import { ReactComponent as EngagementIcon1 } from '../../assets/engagement.svg';
import { ReactComponent as SimilarityIcon } from '../../assets/similarity.svg';
import { ReactComponent as FollowersIcon } from '../../assets/followers.svg';
import { ReactComponent as TranslationIcon } from '../../assets/translation.svg';
import { ReactComponent as OriginalTextIcon } from '../../assets/originalText.svg';
import { ReactComponent as ActorIcon } from '../../pages/SearchPage/assets/link.svg';
import { ReactComponent as SourceIcon } from '../../pages/SearchPage/assets/person.svg';
import { ReactComponent as SearchIcon } from '../../pages/SearchPage/assets/search.svg';
import { ReactComponent as AddToGroupIcon } from '../Layout/assets/database.svg';
import { ReactComponent as EditIcon } from '../../assets/edit.svg';
import { ReactComponent as SettingsIcon } from '../../assets/settings.svg';
import { decodeSourceName } from '../../utils/decodeURI';
import {platformIconsMapping, platformNamesMapping} from '../../utils/platforms';
import {contentTypesIconsMapping, contentTypesMapping} from '../../utils/contentTypes';
import { ExpandableText } from '../../components/CollapsibleText/ExpandableText';
import { useCurrentUser } from '../../contexts/CurrentUser';
import { GraphWidget } from '../../components/GraphWidget/GraphWidget';
import { useSavedGraph } from '../../components/GraphWidget/useSavedGraph';
import { GraphViewOverlay } from '../SocialGraphPOC/GraphViewOverlay';
import { cutString } from '../../utils/cutString';
import { MessageModal } from '../../components/MessagesTable/MessageModal';
import { replaceNewlinesWithBreaks } from '../../utils/replaceNewlinesWithBreaks';
import { ImagesContainer } from '../../components/Image/ImagesContainer';
import { mapCompromisedEntityTypes, mapBotEntityTypes, mapAffiliationEntityTypes } from '../../utils/actors';
import { DropdownMenu } from '../../components/DropdownMenu/DropdownMenu';
import { ButtonsList } from '../../components/DropdownMenu/ButtonsList';
import { LinkedActors } from './LinkedActors/LinkedActors.jsx';
import { TagChip, getTagLevel2Name, getTagProof } from '../../components/TagsTree';
import { formatNumber, formatWithCustomGigaBillion } from '../../utils/fomatNumbers';
import { AddToSourceGroupModal } from '../../components/AddToSourceGroupModal/AddToSourceGroupModal';
import { MessageInfoWrapper } from '../../components/MessagesTable/MessageInfoWrapper';
import { MessageMetricsWrapper } from '../../components/MessagesTable/MessageMetricsWrapper';
import { OriginalTextBlock } from '../../components/MessagesTable/OriginalTextBlock';
import { MessageTooltips } from '../../components/MessagesTable/MessageTooltips';

import messageStyles from "../../components/MessagesTable/ExtendedMessageView.module.scss";
import { showRequestError } from '../../utils/requestError';

const formatRate = d3.format('.2f')

// window for the "Last publications" block; the search endpoint requires a date range
const LAST_PUBLICATIONS_DAYS = 30;
// when the last-N-days block is empty, "See older publications" searches the
// year that ends where this block's window starts
const OLDER_PUBLICATIONS_DAYS = 365;

export const SourcePage = ({}) => {
  const { t } = useTranslation();
  const { sourceId } = useParams();
  const [messagesModal, setMessagesModal] = useState({isOpen: false, narrativeId: null});
  const [source, setSource] = useState(null);
  const [botInfo, setBotInfo] = useState(null);
  const [narratives, setNarratives] = useState(null);
  const [threats, setThreats] = useState(null);
  const [stats, setStats] = useState(null);
  const [affiliations, setAffiliations] = useState(null);
  const [discreditations, setDiscreditations] = useState(null);
  const [narrativesPage, setNarrativesPage] = useState(1);
  const [threatsPage, setThreatsPage] = useState(1);
  const [messages, setMessages] = useState(null);
  // a failed request must not look like an actor with no publications
  const [messagesError, setMessagesError] = useState(false);
  const [messagesAttempt, setMessagesAttempt] = useState(0);
  const [narrativesWidget, setNarrativesWidget] = useState(null);
  const [messageModal, setMessageModal] = useState({message: null, isOpen: false, narrartiveMessage: null});
  const [isMessageDropdownOpen, setIsMessageDropdownOpen] = useState({});
  const [error, setError] = useState(null);
  const [addToGroupModal, setAddToGroupModal] = useState({
    isOpen: false,
  });


  const [currentUser] = useCurrentUser();
  const workspaceConfig = currentUser?.workspace?.config;
  const featureFlags = workspaceConfig?.featureflags;
  const nativeLang = currentUser?.translation_language ? currentUser?.translation_language : window.clientInformation.language.split('-', 1)[0].toUpperCase();
  const showSiilaritySearch = featureFlags?.includes('show_similarity_search');
  const hideRiskActors = featureFlags?.includes('hide_risk_actors');
  // the flag gates the hook too, not just the render: a social-graph request from
  // a flag-less workspace gets a 403, which API.fetch treats as an expired session
  const showGraphWidget = featureFlags?.includes('social_graph');
  const graphWidget = useSavedGraph('actor', sourceId, Boolean(showGraphWidget));
  // graph opens in an overlay (not a route) so closing it keeps the user's
  // place on this page — scroll position, filters, pagination
  const [isGraphOverlayOpen, setIsGraphOverlayOpen] = useState(false);

  const size = 10;

  const [highlights, setHighlights] = useState(['KEYWORDS']);

  const fetchSource = useCallback(() => {
    API.fetch('GET', `/API/v1/sources/${sourceId}`).then(setSource).catch(error => {
      setSource(null);
      if (error.message === 'Client error: Source not found') {
        setError(t('Actor not found'));
      } else {
        setError(t('Failed to fetch actor'));
      }
    });
  }, [sourceId]);

  const fetchCasesAndThreatsData = useCallback(() => {
    Promise.all([
      API.fetch('GET', `/API/v1/narratives?source_id=${sourceId}&sorting=-date_created&is_threat=false&page=${narrativesPage}&size=${size}`),
      API.fetch('GET', `/API/v1/narratives?source_id=${sourceId}&sorting=-date_created&is_threat=true&page=${threatsPage}&size=${size}`)
    ]).then(([casesData, threatsData]) => {

      if(casesData.objects?.length > 0) {
        setNarrativesWidget('cases')
      } else if(threatsData.objects?.length > 0) {
        setNarrativesWidget('threats')
      } else {
        setNarrativesWidget('cases')
      }

      setNarratives(casesData);
      setThreats(threatsData);
    }).catch(error => {
      showRequestError(error, t('Cases and threats'));
      setNarratives({ objects: [] });
      setThreats({ objects: [] });
      setNarrativesWidget('cases');
    });
  }, [sourceId]);
  
  useEffect(() => {
    fetchCasesAndThreatsData();
  }, [fetchCasesAndThreatsData]);

  const fetchNarratives = (page) => {
    setNarratives(null)
    API.fetch('GET', `/API/v1/narratives?source_id=${sourceId}&sorting=-date_created&is_threat=false&page=${page}&size=${size}`).then(setNarratives).catch((e) => { showRequestError(e, t('Cases')); setNarratives({ objects: [] }); });
  };

  const fetchThreats = (page) => {
    setThreats(null)
    API.fetch('GET', `/API/v1/narratives?source_id=${sourceId}&sorting=-date_created&is_threat=true&page=${page}&size=${size}`).then(setThreats).catch((e) => { showRequestError(e, t('Threats')); setThreats({ objects: [] }); });
  };

  // `source` still holds the previous actor for one render after the URL changes
  const sourceType = source && String(source.id) === String(sourceId) ? source.source_type : undefined;
  // content types requested for the last publications; the search links below
  // carry the same filter so the search page shows the same kind of content
  const lastPublicationsContentTypes = useMemo(
    () => (sourceType === 'TELEGRAM' ? ['POST'] : null),
    [sourceType],
  );

  const fetchMessages = useCallback((signal) => {
    setMessages(null);
    setMessagesError(false);
    const urlParams = new URLSearchParams();
    urlParams.set('size', 5);
    urlParams.set('sorting', '-published_at');

    const today = new Date();
    const bodyRequest = {
      source_ids: [sourceId],
      start_date: format(subDays(today, LAST_PUBLICATIONS_DAYS), 'yyyy-LL-dd'),
      end_date: format(today, 'yyyy-LL-dd'),
    };

    if (lastPublicationsContentTypes) {
      bodyRequest.content_types = lastPublicationsContentTypes;
    }

    API.fetch('POST', `/API/v1/messages/search?${urlParams.toString()}`, null, bodyRequest, signal)
      .then((data) => {
        if (signal?.aborted) return;
        setMessages(data);
      })
      .catch((e) => {
        // a newer request owns the state now
        if (signal?.aborted) return;
        showRequestError(e, t('Last publications'));
        setMessagesError(true);
        setMessages({ objects: [] });
      });
  }, [sourceId, lastPublicationsContentTypes]);

  const fetchStatsAndAffiliations = useCallback(() => {
    setStats(null);
    setAffiliations(null);
    setDiscreditations(null);

    Promise.all([
      API.fetch('GET', `/API/v1/sources/${sourceId}/stats`),
      API.fetch('GET', `/API/v1/sources/${sourceId}/affiliations`),
      API.fetch('GET', `/API/v1/sources/${sourceId}/discreditations`)
    ]).then(([stats, affiliations, discreditations]) => {
      setStats(stats);
      setAffiliations(affiliations);
      setDiscreditations(discreditations);
    }).catch((e) => {
      showRequestError(e, t('Actor details'));
      setStats({});
      setAffiliations({});
      setDiscreditations({});
    });
  }, [sourceId]);

  const checkIfBot = () => {
    API.fetch('GET', `/API/v2/actors/${sourceId}/bot-info`)
      .then(data => {
        setBotInfo(data);
      })
      .catch(error => {
        showRequestError(error, t('Inauthentic behavior'));
        setBotInfo(null);
      });
  };

  useEffect(() => {
    if(source) {
      checkIfBot();
    }
  }, [source]);

  useEffect(() => {
    setSource(null)
    fetchSource()
  }, [sourceId]);

  
  useEffect(() => {
    // wait for the source so the TELEGRAM content-type filter is known before the first request
    if (!sourceType) return undefined;
    const controller = new AbortController();
    fetchMessages(controller.signal);
    // a source switch or unmount cancels the in-flight request so its response cannot land late
    return () => controller.abort();
  }, [fetchMessages, sourceType, messagesAttempt]);

  useEffect(() => {
    setNarrativesPage(1);
    setThreatsPage(1);
  }, [sourceId]);

  useEffect(() => {
    fetchStatsAndAffiliations();
  }, [fetchStatsAndAffiliations])

  if(!source && error) {
    return <div className='noData'>{t(error)}</div>
  }

  if (!source || !sourceId) {
    return <Loader />;
  }

  // const sourceData = data[(source.id in data) ? source.id : 0];

  const statusMapping = {
    NEW: {class: messageStyles.new, name: 'New'},
    READ: {class: messageStyles.approved, name: 'Approved'},
    DELETED: {class: messageStyles.deleted, name: 'Deleted'}
  }

  const getBotReason = (reason) => {
    const botReasonMap = {
      STATISTICS: t(
        "Flagged for posting comments identical to other accounts, suggesting multi-account coordination"
      ),
      SIMILARITY: t(
        "Flagged for repeatedly posting identical comments, indicating automated behavior"
      ),
      SUSPENDED_BY_PLATFORM: t('This account has been suspended by a platform'),
      MANUAL: t('This account was labeled through human review'),
      IMMATURE_ACCOUNT: t('This account was recently created or has few followers'),
    };

    return botReasonMap[reason] ?? t('N/A');
  };

  const getBotType = (source) => {
    if(source?.suspended_by_platform === true) {
      return mapBotEntityTypes(t)['suspended_by_platform'];
    } else if(source?.inauthentic_behavior === true || source?.is_bot === true) {
      return mapBotEntityTypes(t)['inauthentic_behavior'];
    } else if(source?.immature_account === true) {
      return mapBotEntityTypes(t)['immature_account'];
    } else {
      return null;
    }
  }

  let followers_cnt = source?.audience;
  let botType = getBotType(source);

  let followersCountHeader = 'Audience';
  let averageReachHeader = 'Average post reach';
  let engagementHeader = 'Engagement rate';

  if(source.source_type === 'WEB') {
    followersCountHeader = 'Monthly audience';
    averageReachHeader = null;
    engagementHeader = null;
  }

  if(source.source_type === 'YOUTUBE') {
    averageReachHeader = 'Average video reach';
  }

  if(source.source_type === 'TELEGRAM') {
    engagementHeader = 'Engagement / views';
  }

  if(source.source_type === 'FACEBOOK') {
    engagementHeader = 'Engagement / followers';
    averageReachHeader = 'Average post engagement';
  }

  const isUkLocale =
    localStorage.getItem('i18nextLng') === 'ua-UK' ||
    localStorage.getItem('i18nextLng') === 'ru-RU' ||
    localStorage.getItem('i18nextLng') === 'ru' ||
    localStorage.getItem('i18nextLng') === 'uk';

  // fall back to the other language so a proof never renders with empty text
  const localizedProofName = (proof) =>
    isUkLocale
      ? proof?.proof_name_uk || proof?.proof_name_en
      : proof?.proof_name_en || proof?.proof_name_uk;

  const affiliation = affiliations?.length ? affiliations[0] : null;
  const affilliationProofName = localizedProofName(affiliation);

  const discreditation = discreditations?.length ? discreditations[0] : null;
  const discreditationProofName = localizedProofName(discreditation);

  // backend attaches the proof of the deepest tag level that has one, or nulls
  const tagProofs = (source?.tags || [])
    .map((tag) => ({ tag, proof: getTagProof(tag) }))
    .filter(({ proof }) => proof !== null);

  const makeAvatar = (sourceName) => {
    return sourceName.charAt(0).toUpperCase()
  }

  const translateMessage = (messageId, text, nativeLang, sourceLang='') => {
    let body;
    if(sourceLang) {
      body = {
        text: text,
        destination_language: nativeLang,
        source_language: sourceLang
      }
    } else {
      body = {
        text: text,
        destination_language: nativeLang,
      }
    }
    return API.fetch('POST', '/API/v1/translations/translate', null, body)
  }

  const toggleTranslate = (messageId, isTranslation) => {
    setMessages({...messages, objects: messages.objects.map(message => {
      if(message.id === messageId) {
        message.isTranslation = isTranslation;
        if (message.content_type === 'COMMENT' && message.parent_message) {
          message.parent_message.isTranslation = isTranslation;
        }
        return message;
      } else {
        return message;
      }
    })})
  }

  const handleTranslation = (messageId, text, isTranslation) => {
    const target = messages.objects.find(message => message.id === messageId);
    const originalPostText =
      target?.content_type === 'COMMENT' ? target?.parent_message?.highlighted_text || target?.parent_message?.text : '';
    if (target.translated) {
      toggleTranslate(messageId, isTranslation)
    } else {
      toggleTranslate(messageId, true)
      const nativeLanguage = currentUser?.translation_language
        ? currentUser?.translation_language
        : window.clientInformation.language.split('-', 1)[0];

      let newMessagesList = [...messages.objects];

      if (originalPostText) {
        Promise.allSettled([
          translateMessage(messageId, originalPostText, nativeLanguage),
          translateMessage(messageId, text, nativeLanguage),
        ]).then((results) => {
          if (results[0].status === 'fulfilled') {
            const originalPostData = results[0].value;

            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                let m = {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: originalPostData.destination_text,
                  },
                };
                return m;
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  parent_message: {
                    ...message.parent_message,
                    isTranslation: true,
                    translated: t('We were not able to translate this text.'),
                    error: true,
                  },
                };
              } else {
                return message;
              }
            });
          }

          if (results[1].status === 'fulfilled') {
            const messageData = results[1].value;
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: messageData.destination_text,
                };
              } else {
                return message;
              }
            });
          } else {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });
          }

          setMessages({ ...messages, objects: newMessagesList });
        });
      } else {
        translateMessage(messageId, text, nativeLanguage)
          .then((data) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: data.destination_text,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          })
          .catch((e) => {
            newMessagesList = newMessagesList.map((message) => {
              if (message.id === messageId) {
                return {
                  ...message,
                  isTranslation: true,
                  translated: t('We were not able to translate this text.'),
                  error: true,
                };
              } else {
                return message;
              }
            });

            setMessages({ ...messages, objects: newMessagesList });
          });
      }
    }
  }

  const buildSearchLink = (startDate, endDate) => {
    const params = new URLSearchParams();
    params.set('source_ids', sourceId);
    lastPublicationsContentTypes?.forEach((contentType) => {
      params.append('content_types', contentType);
    });
    params.set('actor_search', sourceId);
    params.set('start_date', format(startDate, 'yyyy-LL-dd'));
    params.set('end_date', format(endDate, 'yyyy-LL-dd'));
    return `/search?${params.toString()}`;
  };

  // "See more publications" opens the same window the block was fetched with
  const today = new Date();
  const messagesLink = buildSearchLink(subDays(today, LAST_PUBLICATIONS_DAYS), today);
  const olderPublicationsEnd = subDays(today, LAST_PUBLICATIONS_DAYS);
  const olderPublicationsLink = buildSearchLink(
    subDays(olderPublicationsEnd, OLDER_PUBLICATIONS_DAYS),
    olderPublicationsEnd,
  );

  const getThreatName = (threat) => {
    if (!threat?.is_public) {
      return threat?.name
    }

    let public_name;
    if(
      localStorage.getItem('i18nextLng') === 'ua-UK' ||
      localStorage.getItem('i18nextLng') === 'ru-RU' ||
      localStorage.getItem('i18nextLng') === 'ru' ||
      localStorage.getItem('i18nextLng') === 'uk'
    ) {
      public_name = threat?.public_name_uk;
    } else {
      public_name = threat?.public_name_en;
    }

    return public_name ?? threat?.name
  }

  return (
    <div className={styles.root}>
      <div className="page-header">
        <div className="breadcrumb">
          <div className="breadcrumb">
            <span>
              <Link to="/sources">{t('Actors')}</Link>
            </span>
            <span>{decodeSourceName(source.name)}</span>
          </div>
          
        </div>
        <div className="controls">
          <Tooltip content={t('Add to actor group')} position='left'>
            <button
              className={cn(styles.addToGroupButton, 'new-button')}
              onClick={() => {
                setAddToGroupModal({ isOpen: true  })
              }}
            >
              <AddToGroupIcon />
            </button>
          </Tooltip>
          {currentUser?.is_super_admin ? <Tooltip content={t('Edit')} position='bottom'>
              <Link
                to={`/sources/${sourceId}/edit`}
                className={cn(styles.buttonText, 'button', 'new-button')}
              >
                <EditIcon />
              </Link>
            </Tooltip> : ''}
          </div>
      </div>

      <div className={styles.header} id='#top'>
        <div className={styles.avatarWrapper}>
          {/* <div className={styles.avatar}><img src={sourceData.avatar}></img></div> */}
          <div className={styles.avatar}>{makeAvatar(decodeSourceName(source.name))}</div>
        </div>
        <div className={styles.headerMain}>
          <div className={styles.headerName}>
            <span>{decodeSourceName(source.name)}</span>
            {source?.is_verified ? (
              <span className={styles.verified}>
                <VerifiedTwitter/>
              </span>
            ) : (
              ''
            )}
          </div>
          <div className={styles.sourceLink}>
            <a href={source?.url} target="_blank" rel="noopener noreferrer">{decodeSourceName(source?.url)}</a>
          </div>
          <div className={styles.headerInfo}>
            <ul className={styles.headerList}>
              <li>
                <span>{t('Actor platform')}:</span> {source?.source_type}
              </li>
              <li>
                <span> {t('Actor type')}:</span>
                {source?.source_type === 'WEB' ? t('WEB') : t('Social media')}
              </li>
              <li>
                <span>{t('Actor origin country')}:</span>{source?.origin_country ? source.origin_country.name : t("N/A")}
                {source?.origin_country && !source?.is_country_set_by_human && <span
                  className={styles.aiBadge}
                  data-tooltip-id="badge-tooltip"
                  data-tooltip-content={`${t('Actor country is AI-detected.')}\n${t('Minor inaccuracies may occur.')}`}
                  data-tooltip-place="right"
                >
                  {t('AI-detected')}
                  <AnchorIcon />
                </span>}

              </li>
              <li>
                <span>{t('Actor main language')}:</span> {source.language ? source.language : t("N/A")}
              </li>
            </ul>
          </div>
          {source?.tags?.length > 0 && (
            <div className={styles.tagsContainer}>
              {source.tags.map((tag) => (
                <TagChip key={tag.full_path} tag={tag} className={styles.tag} />
              ))}
            </div>
          )}
        </div>
        <ReactTooltip
          id="badge-tooltip"
          style={{
            whiteSpace: 'pre-line',
            zIndex: 1000,
            borderRadius: '6px'
          }}
        />
      </div>
      {/*
      {followers_cnt > 0 ? <div className={styles.metric}>
          <div className={styles.metricHeader}>
            <span className={`${styles.metricIcon} ${styles.blue}`}>
              <UserIcon />
            </span>
            <span>{t(followersCountHeader)}</span>
          </div>
          <div className={styles.metricData}>{formatNumberSignificant(followers_cnt)}</div>
        </div> : ''}
      */}

      {
        stats === null || affiliations === null || discreditations === null ? <LoaderSmall/> : <>
          <div className={styles.metrics}>
            {followers_cnt > 0 ? <div className={styles.metric}>
              <div className={styles.metricHeader}>
                <span className={`${styles.metricIcon} ${styles.blue}`}>
                  <UserIcon/>
                </span>
                <span>{t(followersCountHeader)}</span>
              </div>
              <div className={styles.metricData}>{formatWithCustomGigaBillion(followers_cnt)}</div>
            </div> : ''}
            {stats?.last_7d_post_reach > 0 ? <div className={styles.metric}>
              <div className={styles.metricHeader}>
                <span className={`${styles.metricIcon} ${styles.red}`}>
                  <StatsIcon/>
                </span>
                <span>{t(averageReachHeader)}</span>
              </div>
              <div
                className={styles.metricData}>{stats?.last_7d_post_reach >= 1 ? formatWithCustomGigaBillion(stats?.last_7d_post_reach) : formatRate(stats?.last_7d_post_reach)}</div>
            </div> : ''}
            {stats?.last_7d_engagement > 0 ? <div className={styles.metric}>
              <div className={styles.metricHeader}>
            <span className={`${styles.metricIcon} ${styles.green}`}>
              <EngagementIcon/>
            </span>
                <span>{t(engagementHeader)}</span>
              </div>
              <div
                className={styles.metricData}>{stats?.last_7d_engagement > 0 ? formatRate(stats?.last_7d_engagement) : 0}%
              </div>
            </div> : ''}
          </div>

          {showGraphWidget ? (
            <GraphWidget
              title={t('Source exploration graph')}
              description={t('Explore the network around this actor. Find their direct contacts, communities, and role among them.')}
              generatingHint={t("Usually takes 3–20 minutes. You can leave this page, we'll notify you when it's ready.")}
              status={graphWidget.status}
              generatedAt={graphWidget.generatedAt}
              stats={graphWidget.stats}
              isBusy={graphWidget.isBusy}
              showDateRange
              entityName={decodeSourceName(source.name)}
              graphName={graphWidget.graphName}
              graphStartDate={graphWidget.startDate}
              graphEndDate={graphWidget.endDate}
              onGenerate={graphWidget.generate}
              onCancel={graphWidget.cancel}
              onRegenerate={graphWidget.regenerate}
              onOpen={() => graphWidget.graphId && setIsGraphOverlayOpen(true)}
            />
          ) : ''}

          {isGraphOverlayOpen && graphWidget.graphId ? (
            <GraphViewOverlay
              graphId={graphWidget.graphId}
              onClose={() => setIsGraphOverlayOpen(false)}
            />
          ) : ''}

          {tagProofs.length > 0
          ? <div className={styles.sources}>
              <div className={styles.sourcesHeading}>
                {t('Tags')}
              </div>
              <div className={styles.affiliation}>
                {tagProofs.map(({ tag, proof }) => (
                  <div className={styles.affiliationWrapper} key={tag.full_path}>
                    <div className={styles.item}>
                      <div className={styles.itemHeading}>{t('Tag')}</div>
                      <p>{getTagLevel2Name(tag)}</p>
                    </div>
                    <div className={styles.item}>
                      <div className={styles.itemHeading}>{t('Proof')}</div>
                      <p>
                        {proof.link
                          ? <a href={proof.link} target="_blank" rel="noopener noreferrer">
                              {proof.title || cutString(decodeSourceName(proof.link))}
                            </a>
                          : proof.title}
                      </p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          : ''}

          {affiliation !== null
            ? <div className={styles.sources}>
                  <div className={styles.sourcesHeading}>
                    {t('Actor state affiliation')}
                  </div>
                  <div className={styles.affiliation}>
                  <div className={styles.affiliationWrapper}>
                    <div className={styles.item}>
                      <div className={styles.itemHeading}>{t('Country')}</div>
                      <p>{affiliation?.country?.name}</p>
                    </div>
                    <div className={styles.item}>
                      <div className={styles.itemHeading}>{t('Type')}</div>
                      <p>{mapAffiliationEntityTypes(t)[affiliation?.entity_type]}</p>
                    </div>
                    <div className={styles.item}>
                      <div className={styles.itemHeading}>{t('Proof')}</div>
                      <p>
                        {affiliation?.proof_link ? (
                          <a href={affiliation.proof_link} target="_blank" rel="noopener noreferrer">
                            {affilliationProofName || affiliation.proof_link}
                          </a>
                        ) : (
                          affilliationProofName || ''
                        )}
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            : ''}
            {discreditation !== null && !hideRiskActors
              ? <div className={styles.sources}>
                    <div className={styles.sourcesHeading}>
                      {t('Indicators of Risk (IoR)')}
                    </div>
                    <div className={styles.affiliation}>
                    <div className={styles.affiliationWrapper}>
                      <div className={styles.item}>
                        <div className={styles.itemHeading}>{t('Type')}</div>
                        <p>
                          {mapCompromisedEntityTypes(t)[discreditation?.entity_type].title}
                          <span className={styles.tooltipControls}>
                            <Tooltip content={mapCompromisedEntityTypes(t)[discreditation?.entity_type]?.tooltip} position='right'>
                              <AnchorIcon/>
                            </Tooltip>
                          </span>
                        </p>
                      </div>
                      <div className={styles.item}>
                        <div className={styles.itemHeading}>{t('Proof')}</div>
                        <p>
                          {discreditation?.proof_link ? (
                            <a href={discreditation.proof_link} target="_blank" rel="noopener noreferrer">
                              {discreditationProofName || discreditation.proof_link}
                            </a>
                          ) : (
                            discreditationProofName || ''
                          )}
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              : ''}
            {botInfo && botInfo?.is_bot
              ?  <div className={styles.sources}>
                  <div className={styles.sourcesHeading}>
                    {t('Inauthentic behavior')}
                  </div>
                  <div className={styles.affiliation}>
                    <div className={styles.affiliationWrapper}>
                      <div className={styles.item}>
                        <div className={styles.itemHeading}>{t('Reason')}</div>
                        <p>
                          {getBotReason(botInfo?.detection_approach)}

                        </p>

                      </div>
                    </div>
                  </div>
                </div>
              : ''}
        </>
      }

      {/* <div className={styles.politics}>
        <ul className={styles.politicsList}>
          <li>
            <span className={styles.listHeading}>{t('Political compass')}:</span>
            <span
              className={styles.anchor}
              data-tooltip-id="my-tooltip"
              data-tooltip-content={t("The political compass is based on expert analysis of the actor content")}
            >
              <AnchorIcon />
            </span>
            <ReactTooltip
              id="my-tooltip"
              place="top"
              className={styles.tooltip}
            ></ReactTooltip>
            {sourceData.political_compass}
          </li>
         {sourceData.ownership !== null ? <li>
            <span className={styles.listHeading}>{t('Ownership / Holding')}:</span>
            <span
              className={styles.anchor}
              data-tooltip-id="my-tooltip"
              data-tooltip-content="The political compass is based on expert analysis of the actor content"
            >
              <AnchorIcon />
            </span>
            <ReactTooltip
              id="my-tooltip"
              place="top"
              className={styles.tooltip}
            ></ReactTooltip>
            {sourceData.ownership}
          </li> : ''}
        </ul>
      </div> */}

      {/* {Object.keys(sourceData.disinformation).length > 0 ? <div className={styles.campaigns}>
        <div className={styles.campaignsHeading}>
          {t('Actor involvement in disinformation campaigns')}
        </div>
        <div className="list-content">
          <div className="card">
            <div className="card-body">
              <table className={styles.messages}>
                <thead>
                  <tr>
                    <td>
                      <div className={styles.wrapper}>
                        <span>{t('Name')}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.wrapper}>
                        <span>{t('Link')}</span>
                      </div>
                    </td>
                    <td>
                      <div className={styles.wrapper}>
                        <span>{t('Publication')}</span>
                      </div>
                    </td>
                  </tr>
                </thead>

                <tbody>
                  {sourceData.disinformation.map((item) => {
                    return (
                      <tr key={item.id}>
                        <td>{item.name}</td>
                        <td className={styles.link}>
                          <a href={item.link} target="_blank" rel="noreferrer">
                            {item.link}
                          </a>
                        </td>
                        <td className="message">
                          <ExpandableText length={50} text={item.message} />
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      </div> : ''} */}

      

      <div className="report-section">
        <h3 className="trend-chart-controlls">
          {t('Actor background')}
          {narrativesWidget ? <button
            type="button"
            className={narrativesWidget === 'cases' ? 'active' : ''}
            onClick={() => {
              ampli.track({
                event_type: 'Change metric in narratives widget',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  source_id: source.id,
                  metric: 'cases',
                }
              });
              setNarrativesWidget('cases');
            }}
          >
            <span>{t('Cases')}</span>
            <span className={styles.tooltipControls}>
              <Tooltip 
                content={t('Cases where the content from the current actor is tracked')}
                position='right'
                > 
                <AnchorIcon />
              </Tooltip>
            </span>
          </button> : ''}
          {narrativesWidget ? <button
            type="button"
            className={narrativesWidget === 'threats' ? 'active' : ''}
            onClick={() => {
              ampli.track({
                event_type: 'Change metric in narratives widget',
                event_properties: {
                  user_id: currentUser?.id,
                  workspace_id: currentUser?.workspace_id,
                  source_id: source.id,
                  metric: 'threats',
                }
              });
              setNarrativesWidget('threats');
            }}
          >
            <span>{t('Threats')}</span>
            <span className={styles.tooltipControls}>
            <Tooltip 
              content={t('Threats where the content from the current actor is tracked')}
              position='right'
              > 
              <AnchorIcon />
            </Tooltip>
          </span>
          </button> : ''}
          
        </h3>

        <div className="card">
          <div className="">
            {!narratives || !threats 
              ? <div className={styles.card}><Loader/></div>
              : (narratives?.objects?.length > 0 || threats?.objects?.length > 0 ?
              <div className={styles.linkedActorsWrapper}>
                {narrativesWidget === 'cases' && narratives?.objects?.length > 0 ? <div className={styles.sources}>
                
                  <div className={styles.tableWrapper}>
                    <table className={styles.cases} id='narratives'>
                      <thead>
                        <tr>
                          <td>
                            <div className={styles.wrapper}>
                              <span>{t('Case')}</span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.wrapper}>
                              <span>{t('Publications')}</span>
                            </div>
                          </td>
                        </tr>
                      </thead>

                      <tbody>
                        {narratives.objects.map((item) => {
                          return (
                            <tr key={item.id}>
                              <td>
                                <span className={styles.modalLink} onClick={() => setMessagesModal({isOpen: true, narrativeId: item.id})}>{item.name}</span>
                              </td>
                              <td className="message">
                                {formatNumber(item.total_messages)}
                              </td>
                              {/* <td className="link">
                                <span onClick={() => setMessagesModal({isOpen: true, narrativeId: item.id})}><ExpandIcon /></span>
                            </td> */}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                      
                    
                  </div>
                  <div className={styles.paginator}>
                    <Paginator
                      size={size}
                      page={narrativesPage}
                      total={narratives?.total}
                      id='narratives'
                      onPageChange={(page) => {
                        setNarrativesPage(page);
                        fetchNarratives(page);
                      }}
                    />
                  </div>
                </div> : (
                  narrativesWidget === 'cases' && narratives?.objects?.length === 0 
                  ? (
                    <div className={styles.header}>
                      <div className={styles.noData}>{t('There is no data to display')}</div>
                    </div>
                  ) : '')}

                {narrativesWidget === 'threats' && threats?.objects?.length > 0 ? <div className={styles.sources}>
                  <div className={styles.tableWrapper}>
                    <table className={styles.cases} id='threats'>
                      <thead>
                        <tr>
                          <td>
                            <div className={styles.wrapper}>
                              <span>{t('Threat')}</span>
                            </div>
                          </td>
                          <td>
                            <div className={styles.wrapper}>
                              <span>{t('Publications')}</span>
                            </div>
                          </td>
                        </tr>
                      </thead>

                      <tbody>
                        {threats.objects.map((item) => {
                          return (
                            <tr key={item.id}>
                              <td>
                                <span className={styles.modalLink} onClick={() => setMessagesModal({isOpen: true, narrativeId: item.id})}>{ getThreatName(item) }</span>
                              </td>
                              <td className="message">
                                {formatNumber(item.total_messages)}
                              </td>
                              {/* <td className="link">
                                <span onClick={() => setMessagesModal({isOpen: true, narrativeId: item.id})}><ExpandIcon /></span>
                            </td> */}
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                  <div className={styles.paginator}>
                    <Paginator
                      size={size}
                      page={threatsPage}
                      total={threats?.total}
                      id='threats'
                      onPageChange={(page) => {
                        setThreatsPage(page);
                        fetchThreats(page);
                      }}
                    />
                  </div>
                </div> : (
                  narrativesWidget === 'threats' && threats?.objects?.length === 0 
                  ? (
                    <div className={styles.header}>
                      <div className={styles.noData}>{t('There is no data to display')}</div>
                    </div>
                  ) : '')}
              </div> : (
                <div className={styles.header}>
                  <div className={styles.noData}>{t('There is no data to display')}</div>
                </div>)
              )}
          </div>
        </div>
      </div>

      <div className={styles.sources}>
        <div className={styles.sourcesHeading}>
          {t('Publications for the last {{days}} days', { days: LAST_PUBLICATIONS_DAYS })}
        </div>

        {!messages ? <div className={styles.card}><Loader/></div> : messages.objects.length > 0 ? <div>
          <div className="list-content">
            <div className="card source-page-messages">
              {/* <div className="card-body"> */}
              <table className={`${messageStyles.extendedMessages} ${styles.extendedMessages}`}>
                {/* <thead>
                    <tr>
                      <td className={messageStyles.headerInfo}>
                        <span>{t('Total publications')}: {formatNumber(messages.total)}</span>
                      </td>
                      <td className='message-controlls'>
                      </td>
                    </tr>
                  </thead> */}
                <tbody>
                {messages.objects.map(message => {
                  let followers_cnt = message?.source?.audience;
                  let textClass = messageStyles.extendedMessage;
                  let textClassParent = messageStyles.extendedMessage;

                  if (message?.parent_message?.error && message?.parent_message?.isTranslation) {
                    textClassParent = `${messageStyles.extendedMessage} ${messageStyles.error}`
                  } else if (message.error && !message.isTranslation) {
                    textClassParent = messageStyles.extendedMessage;
                  }

                  if (message.error && message.isTranslation) {
                    textClass = `${messageStyles.extendedMessage} ${messageStyles.error}`
                  } else if (message.error && !message.isTranslation) {
                    textClass = messageStyles.extendedMessage;
                  }

                  let messageMenuOptions = []
                  if(showSiilaritySearch) {
                    messageMenuOptions.push({
                      name: t('Find similar publications'),
                      value: 'similar'
                    })
                  }

                  if((message?.content_type === 'COMMENT' && (message?.parent_message || message?.parent_id)) || message?.content_type === 'POST') {
                    messageMenuOptions.push({
                      name: t('Find comments to post'),
                      value: 'comments'
                    })
                  }

                  if (message?.content_type === 'VIDEO' && message?.source?.source_type === 'TIKTOK') {
                    messageMenuOptions.push({
                      name: t('Find comments to video'),
                      value: 'comments_to_video'
                    })
                  }

                  if (message?.content_type === 'POST' && ['TIKTOK', 'FACEBOOK', 'TELEGRAM'].includes(message?.source?.source_type)) {
                    messageMenuOptions.push({
                      name: t('Find reposts to post'),
                      value: 'reposts_to_post'
                    })
                  }

                  const textForTranslation = message.highlighted_text ? replaceNewlinesWithBreaks(message.highlighted_text) : replaceNewlinesWithBreaks(message.message);

                  return (
                    <tr key={message.id}>
                      <td>
                        <MessageInfoWrapper
                          message={message}
                          followers_cnt={followers_cnt}
                          isShare={false}
                          currentUser={currentUser}
                          narrative={null}
                          narrartiveMessage={null}
                          setMessageModal={setMessageModal}
                        />

                        <div className={messageStyles.messageContainer}>
                          <MessageMetricsWrapper
                            message={message}
                            narrative={null}
                            isShare={false}
                            isMessageDropdownOpen={isMessageDropdownOpen}
                            setIsMessageDropdownOpen={setIsMessageDropdownOpen}
                            messageMenuOptions={messageMenuOptions}
                          />

                          {message.parent_message ? (
                            <OriginalTextBlock
                              message={message}
                              isShare={false}
                              currentUser={currentUser}
                              narrative={null}
                              textClassParent={textClassParent}
                            />
                          ) : (
                            ''
                          )}

                          {message?.images && message?.images?.length > 0 ? (
                            <ImagesContainer
                              images={message?.images}
                              showCount={3}
                              className={styles.imagesContainer}
                            />
                          ) : (
                            ''
                          )}

                          <div
                            className={`${messageStyles.textBlock} ${styles.textBlock}`}
                          >
                            {message.isTranslation === true &&
                            !message.translated ? (
                              <div className={messageStyles.loaderContainer}>
                                <LoaderSmall />
                              </div>
                            ) : (
                              <ExpandableText
                                length={150}
                                highlights={highlights}
                                text={
                                  message.isTranslation
                                    ? message.translated
                                    : message.highlighted_text || message.message
                                }
                                textClassName={textClass}
                              />
                            )}
                          </div>
                        </div>
                      </td>
                      <td className="message-controlls">
                        <div className={messageStyles.translationWrapper}>
                          <span
                            className={
                              message?.isTranslation ? '' : messageStyles.active
                            }
                            onClick={() =>
                              handleTranslation(
                                message.id,
                                textForTranslation,
                                false,
                              )
                            }
                          >
                            <OriginalTextIcon />
                          </span>
                          <span
                            className={
                              message?.isTranslation ? messageStyles.active : ''
                            }
                            onClick={() =>
                              handleTranslation(
                                message.id,
                                textForTranslation,
                                true,
                              )
                            }
                          >
                            <TranslationIcon />
                          </span>
                        </div>
                      </td>
                    </tr>
                  );
                })}
                </tbody>
              </table>
              <MessageTooltips scope=".source-page-messages" />
            </div>
            {/* </div> */}
          </div>
          <div className={styles.findMoreContainer}>
            <Link
              to={messagesLink}
              className={`button new-button`}
              target='_blank'
            >
              {t('See more publications')}
            </Link>
          </div>
        </div> : messagesError ? <div className={styles.noPublications}>
          <div className={styles.noPublicationsCard}>
            {t('Failed to load publications')}
          </div>
          <div className={styles.findMoreContainer}>
            <button
              type="button"
              className="button new-button"
              onClick={() => setMessagesAttempt((attempt) => attempt + 1)}
            >
              {t('Try again')}
            </button>
          </div>
        </div> : <div className={styles.noPublications}>
          <div className={styles.noPublicationsCard}>
            {t('No publications found for the last {{days}} days', { days: LAST_PUBLICATIONS_DAYS })}
          </div>
          <div className={styles.findMoreContainer}>
            <Link
              to={olderPublicationsLink}
              className={`button new-button`}
              target='_blank'
            >
              {t('See older publications')}
            </Link>
          </div>
        </div>}
      </div>

      {messagesModal.isOpen ? <MessagesModal
        infoModal={messagesModal.isOpen}
        sourceId={sourceId}
        narrativeId={messagesModal.narrativeId}
        onChange={(bool) => setMessagesModal({isOpen: bool, narrativeId: null})}
      /> : ''}

      {messageModal.isOpen ? <MessageModal 
        message={messageModal.message}
        isOpen={messageModal.isOpen}
        showSentimentIcon={false}
        onChange={(bool, selected) => {
          setMessageModal({message: null, isOpen: bool, narrartiveMessage: null})
        }}
        selected={null}
        narrativeMessage={messageModal.narrartiveMessage}
      /> : ''}
      {addToGroupModal.isOpen ? (
        <AddToSourceGroupModal
          isOpen={addToGroupModal.isOpen}
          sources={[source]}
          onChange={(bool) => {
            setAddToGroupModal({
              isOpen: bool,
            });
          }}
        />
      ) : (
        ''
      )}
    </div>
  );
};
